import React, { useState, useEffect } from "react";
import { useMediaQuery } from "react-responsive";
import { default as calculatorIcon } from "../../../images/calculator.svg";
import { Layout, SelectField } from "../../../components";
import { ibuprofenOptions, paracetamolOptions } from "./utils";
import { PageCategory, Tag } from "../../../core/types";
import { default as syrupIcon } from "../../../images/syrup.svg";
import {
  FormPanel,
  FilledPanel,
  ResultPanel,
  ResultField,
  Disclaimer,
} from "../components";
import ReactSVG from "src/components/ReactSVG";
import { OtherCalculators } from "../OtherCalculators/OtherCalculators";

interface IProps {
  pageContext: {
    categories: PageCategory[];
    tags: Tag[];
  };
}

const SyrupCalculator: React.FC<IProps> = ({
  pageContext: { categories, tags },
}) => {
  const isBigScreen = useMediaQuery({ query: "(min-width: 768px)" });
  const [medicine, setMedicine] = useState("");
  const [medicineVariant, setMedicineVariant] = useState<{
    value: string;
    label: string;
  }>();
  const [childWeight, setChildWeight] = useState<number>();
  const [tempChildWeight, setTempChildWeight] = useState<number>();
  const [result, setResult] = useState<{
    once: string | number;
    dailyDose: string | number;
    dailyVolume: string | number;
  }>({
    once: 0,
    dailyDose: 0,
    dailyVolume: 0,
  });

  useEffect(() => {
    if (medicine && medicineVariant.value && childWeight) {
      if (medicine === "paracetamol") {
        const once = getParacetamolOnce();
        const dailyDose = getParacetamolDailyDose();
        const dailyVolume = getParacetamolDailyVolume();
        setResult({
          once,
          dailyDose,
          dailyVolume,
        });
      } else {
        const once = getIbuprofenOnce();
        const dailyDose = getIbuprofenDailyDose();
        const dailyVolume = getIbuprofenDailyVolume();
        setResult({
          once,
          dailyDose,
          dailyVolume,
        });
      }
    } else {
      setResult({
        once: 0,
        dailyDose: 0,
        dailyVolume: 0,
      });
    }
  }, [medicine, medicineVariant, childWeight]);

  const getIbuprofenOnce = () => {
    const parsedMedicineVariantValue = parseFloat(medicineVariant.value);
    const dosePerKilogram = parsedMedicineVariantValue * 10;
    const dosePerWeight = dosePerKilogram * childWeight;
    const maxDose = parsedMedicineVariantValue * 400;
    const dose = dosePerWeight > maxDose ? maxDose : dosePerWeight;
    return dose.toFixed(1);
  };
  const getIbuprofenDailyDose = () => {
    const dosePerWeight = childWeight * 40;
    const dose = dosePerWeight > 1600 ? 1600 : dosePerWeight;
    return Math.round(dose);
  };
  const getIbuprofenDailyVolume = () => {
    const parsedMedicineVariantValue = parseFloat(medicineVariant.value);
    const dosePerWeight = parsedMedicineVariantValue * childWeight * 40;
    const maxDose = parsedMedicineVariantValue * 1600;
    const dose = dosePerWeight > maxDose ? maxDose : dosePerWeight;
    return dose.toFixed(1);
  };
  const getParacetamolOnce = () => {
    const parsedMedicineVariantValue = parseFloat(medicineVariant.value);
    const dosePerKilogram = parsedMedicineVariantValue * 15;
    const dosePerWeight = dosePerKilogram * childWeight;
    const maxDose = parsedMedicineVariantValue * 1000;
    const dose = dosePerWeight > maxDose ? maxDose : dosePerWeight;
    return dose.toFixed(1);
  };
  const getParacetamolDailyDose = () => {
    const dosePerWeight = childWeight * 90;
    const dose = dosePerWeight > 4000 ? 4000 : dosePerWeight;
    return Math.round(dose);
  };
  const getParacetamolDailyVolume = () => {
    const parsedMedicineVariantValue = parseFloat(medicineVariant.value);
    const dosePerKilogram = parsedMedicineVariantValue * 90;
    const dosePerWeight = dosePerKilogram * childWeight;
    const maxDose = parsedMedicineVariantValue * 4000;
    const dose = dosePerWeight > maxDose ? maxDose : dosePerWeight;
    return dose.toFixed(1);
  };

  return (
    <Layout
      metaTitle="Kalkulator objętości syropu przeciwgorączkowego"
      metaType="article"
      categories={categories}
      tags={tags}
    >
      {/* todo: add breadcrumbs */}
      <div className="container py-4 px-3 md:px-0 mx-auto max-w-3xl">
        <div className="text-blue font-bold flex space-x-3 text-s items-center md:hidden">
          <ReactSVG path={calculatorIcon} />
          <span>Kalkulatory</span>
        </div>
        <div className="md:flex md:justify-between md:items-center md:mb-8">
          <h1 className="text-black text-m md:text-xxl md:leading-tight font-bold mb-3 mt-4">
            Kalkulator objętości syropu przeciwgorączkowego
          </h1>
          <div className="hidden md:flex bg-blue-3 rounded-full w-20 h-20 justify-center items-center flex-shrink-0">
            <ReactSVG className="w-12" path={syrupIcon} />
          </div>
        </div>
        <div className="text-s md:text-m leading-normal md:leading-normal font-semibold text-grey-8 mb-8 md:mb-11">
          Uwaga! Kalkulator ten pełni rolę czysto informacyjną i nie stanowi
          porady medycznej ani zalecenia co do dawkowania leków.
        </div>
        <div className="md:border-2 md:border-grey-10 md:px-14 md:pt-10 md:pb-12">
          {(!medicine || isBigScreen) && (
            <FormPanel label="Wybierz rodzaj syropu">
              <div className="md:flex md:justify-between md:space-x-6">
                <div
                  className={`${
                    medicine === "ibuprofen" && "bg-blue-3"
                  } shadow-3 md:w-2/4 md:text-m rounded-sm text-black h-14 md:h-20 flex justify-center items-center cursor-pointer mb-4`}
                  onClick={() => {
                    setMedicine("ibuprofen");
                    setMedicineVariant({
                      value: "",
                      label: "",
                    });
                    setChildWeight(null);
                  }}
                >
                  Ibuprofen
                </div>
                <div
                  className={`${
                    medicine === "paracetamol" && "bg-blue-3"
                  } shadow-3 md:w-2/4 md:text-m rounded-sm text-black h-14 md:h-20 flex justify-center items-center cursor-pointer`}
                  onClick={() => {
                    setMedicine("paracetamol");
                    setMedicineVariant({
                      value: "",
                      label: "",
                    });
                    setChildWeight(null);
                  }}
                >
                  Paracetamol
                </div>
              </div>
            </FormPanel>
          )}
          {medicine && !isBigScreen && (
            <FilledPanel
              label="Wybierz rodzaj syropu"
              value={medicine}
              onEdit={() => {
                setMedicine("");
                setMedicineVariant({
                  value: "",
                  label: "",
                });
              }}
            />
          )}
          {medicine && (!medicineVariant.value || isBigScreen) && (
            <FormPanel label="Wybierz lek">
              <SelectField
                placeholder="Wybierz lek"
                options={
                  medicine === "ibuprofen"
                    ? ibuprofenOptions
                    : paracetamolOptions
                }
                onChange={value => setMedicineVariant(value)}
                value={medicineVariant}
              />
            </FormPanel>
          )}
          {medicineVariant?.value && !isBigScreen && (
            <FilledPanel
              label="Wybierz lek"
              value={medicineVariant?.label}
              onEdit={() => {
                setMedicineVariant({
                  value: "",
                  label: "",
                });
              }}
            />
          )}
          {medicineVariant?.value && (!childWeight || isBigScreen) && (
            <FormPanel label="Podaj wagę dziecka (w kg)">
              <div className="relative">
                <input
                  type="number"
                  min={0}
                  step="0.1"
                  pattern="[0-9]+([\.,][0-9]+)?"
                  className="form-input rounded w-full border-grey-12 border rounded p-4 placeholder-grey-13"
                  value={tempChildWeight}
                  placeholder="Wprowadź wagę dziecka"
                  onChange={e => setTempChildWeight(e.target.value)
                  }
                />
                <button
                  className="absolute top-2/4 right-4 transform -translate-y-2/4 font-bold"
                  onClick={() => setChildWeight(tempChildWeight)}
                >
                  OK
                </button>
              </div>
            </FormPanel>
          )}
          {childWeight && !isBigScreen && (
            <FilledPanel
              label="Podaj wagę dziecka (w kg)"
              value={childWeight}
              onEdit={() => {
                setChildWeight(null);
              }}
            />
          )}
        </div>
        {!!result.once && (
          <ResultPanel>
            <ResultField
              label="Jednorazowa dawka (objętość)"
              value={`${result.once}ml`}
            />
            <ResultField
              label="Maksymalna dobowa dawka"
              value={`${result.dailyDose}mg`}
            />
            <ResultField
              label="Maksymalna dobowa objętość syropu"
              value={`${result.dailyVolume}ml`}
            />
          </ResultPanel>
        )}
        <Disclaimer />

        <OtherCalculators />
      </div>
    </Layout>
  );
};

export default SyrupCalculator;
